import React, { useState } from 'react';
import { Icon } from '../../Icon';
import { parseBrazilianNumber } from '../../../utils/formatUtils';
import { FormField, ResultCard, EducationalBlock } from './shared';

const FixedIncomeCalculator = ({ onResultChange }: { onResultChange: (data: { label: string, value: string }[]) => void }) => {
  const [initialAmount, setInitialAmount] = useState('10.000,00');
  const [monthlyAmount, setMonthlyAmount] = useState('500,00');
  const [period, setPeriod] = useState('12');
  const [periodType, setPeriodType] = useState<'months' | 'years'>('months');
  const [investmentType, setInvestmentType] = useState<'cdb' | 'lci' | 'lca' | 'tesouro-selic' | 'tesouro-ipca'>('cdb');
  const [rateType, setRateType] = useState<'percent-cdi' | 'ipca-plus' | 'fixed'>('percent-cdi');
  const [rateValue, setRateValue] = useState('110,00');
  const [fixedRate, setFixedRate] = useState('10,50');
  const [result, setResult] = useState<any>(null);

  const calculate = () => {
    const initial = parseFloat(parseBrazilianNumber(initialAmount)) || 0;
    const monthly = parseFloat(parseBrazilianNumber(monthlyAmount)) || 0;
    const months = periodType === 'years' ? (parseFloat(period) || 0) * 12 : (parseFloat(period) || 0);

    // Taxa anual baseada no tipo
    let annualRate = 0;
    const cdiRate = 0.108; // CDI atual ~10.8% ao ano
    const ipcaRate = 0.0369; // IPCA acumulado 12 meses ~3.69%

    switch (rateType) {
      case 'percent-cdi':
        annualRate = cdiRate * (parseFloat(rateValue) || 0) / 100;
        break;
      case 'ipca-plus':
        annualRate = ipcaRate + (parseFloat(rateValue) || 0) / 100;
        break;
      case 'fixed':
        annualRate = (parseFloat(fixedRate) || 0) / 100;
        break;
    }

    // Cálculo de juros compostos com aportes mensais
    let total = initial;
    const monthlyRate = Math.pow(1 + annualRate, 1 / 12) - 1;

    for (let i = 0; i < months; i++) {
      total = total * (1 + monthlyRate) + monthly;
    }

    const totalInvested = initial + (monthly * months);
    const grossEarnings = total - totalInvested;

    // Cálculo de imposto de renda (baseado no prazo)
    let taxRate = 0.225; // Padrão 22.5%
    if (months <= 6) taxRate = 0.225;
    else if (months <= 12) taxRate = 0.20;
    else if (months <= 24) taxRate = 0.175;
    else taxRate = 0.15;

    // LCI e LCA são isentos de IR
    const isTaxFree = investmentType === 'lci' || investmentType === 'lca';
    const incomeTax = isTaxFree ? 0 : grossEarnings * taxRate;
    const netEarnings = grossEarnings - incomeTax;
    const netTotal = totalInvested + netEarnings;

    // Rentabilidade líquida anual
    const effectiveAnnualRate = Math.pow(netTotal / totalInvested, 12 / months) - 1;

    let res = {
      totalInvested,
      grossEarnings,
      incomeTax,
      netEarnings,
      netTotal,
      effectiveAnnualRate: effectiveAnnualRate * 100,
      monthlyRate: monthlyRate * 100,
      annualRate: annualRate * 100,
      taxRate: taxRate * 100,
      isTaxFree
    };

    setResult(res);

    onResultChange([
      { label: 'Total Investido', value: `R$ ${totalInvested.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Rendimento Bruto', value: `R$ ${grossEarnings.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Imposto de Renda', value: `R$ ${incomeTax.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} (${isTaxFree ? 'Isento' : (taxRate * 100).toFixed(1) + '%'})` },
      { label: 'Total Líquido', value: `R$ ${netTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Rentabilidade Líquida', value: `${(effectiveAnnualRate * 100).toFixed(2)}% a.a.` }
    ]);
  };

  return (
    <div className="space-y-8">
      <div className="bg-theme-result p-8 rounded-3xl shadow-lg border border-theme-border">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          <FormField
            label="Valor Inicial (R$)"
            placeholder="Ex: 10000"
            value={initialAmount}
            onChange={setInitialAmount}
            required
          />
          <FormField
            label="Aporte Mensal (R$)"
            placeholder="Ex: 500"
            value={monthlyAmount}
            onChange={setMonthlyAmount}
          />
          <div className="grid grid-cols-2 gap-3">
            <FormField
              label="Prazo"
              placeholder="Ex: 12"
              value={period}
              onChange={setPeriod}
              required
            />
            <FormField
              label="Período"
              value={periodType}
              onChange={setPeriodType}
              type="select"
              required
              options={[
                { value: 'months', label: 'Meses' },
                { value: 'years', label: 'Anos' }
              ]}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          <FormField
            label="Tipo de Investimento"
            value={investmentType}
            onChange={setInvestmentType}
            type="select"
            required
            options={[
              { value: 'cdb', label: 'CDB' },
              { value: 'lci', label: 'LCI' },
              { value: 'lca', label: 'LCA' },
              { value: 'tesouro-selic', label: 'Tesouro Selic' },
              { value: 'tesouro-ipca', label: 'Tesouro IPCA+' }
            ]}
          />
          <FormField
            label="Tipo de Rentabilidade"
            value={rateType}
            onChange={setRateType}
            type="select"
            required
            options={[
              { value: 'percent-cdi', label: '% do CDI' },
              { value: 'ipca-plus', label: 'IPCA + %' },
              { value: 'fixed', label: 'Taxa Fixa % a.a.' }
            ]}
          />
          {rateType === 'percent-cdi' && (
            <FormField
              label="% do CDI"
              placeholder="Ex: 110"
              value={rateValue}
              onChange={setRateValue}
              required
              helpText="CDI atual: ~10.8% a.a."
            />
          )}
          {rateType === 'ipca-plus' && (
            <FormField
              label="Taxa Real (%)"
              placeholder="Ex: 5"
              value={rateValue}
              onChange={setRateValue}
              required
              helpText="IPCA atual: ~3.69% a.a."
            />
          )}
          {rateType === 'fixed' && (
            <FormField
              label="Taxa Anual (%)"
              placeholder="Ex: 10,50"
              value={fixedRate}
              onChange={setFixedRate}
              required
              isNumeric
              type="text"
            />
          )}
        </div>

        <div className="flex justify-center mb-8">
          <button
            onClick={calculate}
            className="px-12 py-4 bg-accent-gradient text-white rounded-xl font-bold hover:shadow-accent-hover hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
          >
            <Icon name="play" className="w-5 h-5" />
            Calcular Investimento
          </button>
        </div>

        {result && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <ResultCard
                title="Total Investido"
                value={`R$ ${result.totalInvested.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                color="slate"
              />
              <ResultCard
                title="Rendimento Bruto"
                value={`R$ ${result.grossEarnings.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                color="blue"
              />
              <ResultCard
                title="Imposto de Renda"
                value={`R$ ${result.incomeTax.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                description={result.isTaxFree ? 'Isento' : `${result.taxRate.toFixed(1)}%`}
                color="amber"
              />
              <ResultCard
                title="Total Líquido"
                value={`R$ ${result.netTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                color="emerald"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              <ResultCard
                title="Rentabilidade Líquida Anual"
                value={`${result.effectiveAnnualRate.toFixed(2)}% a.a.`}
                color="purple"
              />
              <ResultCard
                title="Taxa Mensal Efetiva"
                value={`${result.monthlyRate.toFixed(4)}% a.m.`}
                color="indigo"
              />
              <ResultCard
                title="Ganho Líquido"
                value={`R$ ${result.netEarnings.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                description={`${((result.netEarnings / result.totalInvested) * 100).toFixed(2)}%`}
                color="green"
              />
            </div>
          </>
        )}
      </div>

      <EducationalBlock
        title="Sobre Investimentos em Renda Fixa"
        tips={[
          'CDB: Certificado de Depósito Bancário, tributável pelo IR',
          'LCI/LCA: Letras de Crédito, isentas de Imposto de Renda',
          'Tesouro Selic: Rende conforme taxa Selic, tributável',
          'Tesouro IPCA+: Protege contra inflação, tributável',
          'IR regressivo: Quanto maior o prazo, menor a alíquota',
          '% CDI: Proporção da taxa CDI (atualmente ~10.8% a.a.)',
          'IPCA +: Taxa real + inflação, protege poder de compra'
        ]}
      />
    </div>
  );
};

export default FixedIncomeCalculator;
